import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiShield, FiCpu, FiBook, FiTarget, FiArrowRight } from 'react-icons/fi';

const DefensivePublications = () => {
  const publications = [
    {
      id: 'plutus',
      icon: FiCpu,
      title: 'Defensive Publication - Plutus',
      subtitle: 'AI-Powered Risk Officer',
      description: 'AI-Based Institutional Pre-Trade Risk Governance with Behavioral Context, Rule Enforcement, and Capital Protection Logic. A voice-enabled AI risk officer that evaluates trades before execution using deterministic policy rules and behavioral intelligence.',
      path: '/legal/defensive-publication-plutus',
      date: 'December 14th, 2025'
    },
    {
      id: 'journal',
      icon: FiBook,
      title: 'Defensive Publication - Trading Journal',
      subtitle: 'Institutional Trading Journal',
      description: 'AI-Based Institutional Trading Journal with Behavioral Memory, Rule-Adherence Scoring, and Pre/Post-Trade Governance. A structured journaling system that captures multi-modal trader inputs and normalizes them into institutional templates with plan-compliance auditing.',
      path: '/legal/defensive-publication-journal',
      date: 'December 14th, 2025'
    },
    {
      id: 'tdi',
      icon: FiTarget,
      title: 'Defensive Publication - TDI',
      subtitle: 'Trader Discipline Index',
      description: 'Behavioral Discipline Scoring for Trading Decision Quality, Risk Adherence, and Process Integrity. A multi-factor discipline score that measures decision quality and process integrity over time, enabling consistent coaching and governance independent of market outcomes.',
      path: '/legal/defensive-publication-tdi',
      date: 'December 14th, 2025'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-gray-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors"
          >
            <FiArrowLeft className="text-lg" />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
              <FiShield className="text-2xl text-cyan-400" />
            </div>
            <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-full border border-cyan-500/30">
              Prior Art Documentation
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Defensive Publications
          </h1>
          <p className="text-xl text-gray-400 leading-relaxed">
            These documents are published as defensive prior art to prevent the patenting of the described concepts by third parties.
          </p>
        </div>

        {/* Publications List */}
        <div className="space-y-6">
          {publications.map((pub) => (
            <Link
              key={pub.id}
              to={pub.path}
              className="block p-6 bg-gray-800/50 rounded-xl border border-white/10 hover:border-purple-500/30 hover:bg-gray-800/70 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 group-hover:scale-110 transition-transform">
                  <pub.icon className="text-2xl text-cyan-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-white group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-cyan-400 group-hover:bg-clip-text group-hover:text-transparent transition-all">
                      {pub.title}
                    </h2>
                    <FiArrowRight className="text-gray-500 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
                  </div>
                  <p className="text-cyan-400 text-sm font-medium mb-2">{pub.subtitle}</p>
                  <p className="text-gray-400 leading-relaxed mb-3">{pub.description}</p>
                  <p className="text-gray-500 text-sm">Published: {pub.date}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Legal Notice */}
        <div className="mt-12 p-6 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 rounded-xl border border-purple-500/30">
          <h3 className="text-lg font-bold text-white mb-3">Legal Notice</h3>
          <p className="text-gray-400 text-sm leading-relaxed">
            These publications intentionally omit model architectures, prompt structures, scoring algorithms, data schemas, threshold values, and implementation details. These elements remain protected as trade secrets. The purpose of these publications is to establish prior art and prevent third-party patenting of the described concepts.
          </p>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <p className="text-gray-500 text-sm text-center">
            © 2025 Quantum AI Fintech Corporation SA. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DefensivePublications;
